import 'whatwg-fetch';
import {
  searchResultsTemplate,
  numberOfResultsTemplateFct,
  loadingResultsMessageTemplateFct,
} from '../../templates/searchResultsTemplate';
import { objectToQueryParams } from '../../utils/objectToQueryParams';
import { theme } from '../../constants/defaults';

const options = {
  TYPES: {
    PRODUCT: 'product',
    PAGE: 'page',
    ARTICLE: 'article',
  },
  FIELDS: {
    AUTHOR: 'author',
    BODY: 'body',
    PRODUCT_TYPE: 'product_type',
    TAG: 'tag',
    TITLE: 'title',
    VARIANTS_BARCODE: 'variants.barcode',
    VARIANTS_SKU: 'variants.sku',
    VARIANTS_TITLE: 'variants.title',
    VENDOR: 'vendor',
  },
  UNAVAILABLE_PRODUCTS: {
    SHOW: 'show',
    HIDE: 'hide',
    LAST: 'last',
  },
};

const config = {
  resources: {
    type: [options.TYPES.PRODUCT, options.TYPES.ARTICLE],
    options: {
      unavailable_products: options.UNAVAILABLE_PRODUCTS.LAST,
      fields: [
        options.FIELDS.TITLE,
        options.FIELDS.VENDOR,
        options.FIELDS.PRODUCT_TYPE,
        options.FIELDS.VARIANTS_TITLE,
      ],
    },
  },
};
const configBlog = {
  resources: {
    type: [options.TYPES.ARTICLE],
    options: {
      unavailable_products: options.UNAVAILABLE_PRODUCTS.LAST,
      fields: [options.FIELDS.TITLE, options.FIELDS.VENDOR],
    },
  },
};

export default class PredictiveSearch {
  constructor(el, blogSearch = false) {
    this.isResultVisible = false;
    this.results = {};
    this.latencyTimer = null;
    this.resultNodeClicked = false;
    this.configParams = objectToQueryParams(config);
    if (blogSearch) {
      this.configParams = objectToQueryParams(configBlog);
    }
    // Find nodes
    this.nodes = {
      input: el.querySelector('[data-predictive-search-input]'),
      reset: el.querySelector('[data-predictive-search-clear]'),
      result: el.querySelector('[data-predictive-search-result]'),
    };

    // Store the keyword that was used for the search
    this.searchKeyword = '';

    // Assign number of results template
    this.numberOfResultsTemplateFct = numberOfResultsTemplateFct;

    // Assign loading state template function
    this.loadingResultsMessageTemplateFct = loadingResultsMessageTemplateFct;

    // Assign number of search results
    this.numberOfResults = 4;

    // Set classes
    this.classes = {
      visibleVariant: 'predictive-search-wrapper--visible',
      itemSelected: 'predictive-search-item--selected',
      clearButtonVisible: 'predictive-search__clear-button--visible',
    };

    this.selectors = {
      searchResult: '[data-search-result]',
    };

    // Add input attributes
    this.addInputAttributes(this.nodes.input);

    // Add input event listeners
    this.addInputEventListeners();

    // Add body listener
    this.addBodyEventListener();

    // Add accessibility announcer
    this.addAccessibilityAnnouncer();

    // Display the reset button if the input is not empty
    this.toggleClearButtonVisibility();
  }

  addInputAttributes(input) {
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('spellcheck', 'false');
  }

  removeInputAttributes(input) {
    input.removeAttribute('autocorrect', 'off');
    input.removeAttribute('autocomplete', 'off');
    input.removeAttribute('autocapitalize', 'off');
    input.removeAttribute('spellcheck', 'false');
  }

  addInputEventListeners() {
    const { input, reset } = this.nodes;

    if (!input) {
      return;
    }

    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.handleInputBlur = this.handleInputBlur.bind(this);
    this.handleInputKeyup = this.handleInputKeyup.bind(this);
    this.handleInputKeydown = this.handleInputKeydown.bind(this);

    input.addEventListener('focus', this.handleInputFocus);
    input.addEventListener('blur', this.handleInputBlur);
    input.addEventListener('keyup', this.handleInputKeyup);
    input.addEventListener('keydown', this.handleInputKeydown);

    if (reset) {
      this.handleInputReset = this.handleInputReset.bind(this);
      reset.addEventListener('click', this.handleInputReset);
    }
  }

  removeInputEventListeners() {
    const { input } = this.nodes;

    input.removeEventListener('focus', this.handleInputFocus);
    input.removeEventListener('blur', this.handleInputBlur);
    input.removeEventListener('keyup', this.handleInputKeyup);
    input.removeEventListener('keydown', this.handleInputKeydown);
  }

  addBodyEventListener() {
    this.handleBodyMousedown = this.handleBodyMousedown.bind(this);

    document.querySelector('body').addEventListener('mousedown', this.handleBodyMousedown);
  }

  removeBodyEventListener() {
    document.querySelector('body').removeEventListener('mousedown', this.handleBodyMousedown);
  }

  removeClearButtonEventListener() {
    const { reset } = this.nodes;

    if (!reset) {
      return;
    }

    reset.removeEventListener('click', this.handleInputReset);
  }

  // Event handlers
  handleBodyMousedown(evt) {
    if (this.isResultVisible && this.nodes !== null) {
      if (
        evt.target.isEqualNode(this.nodes.input) ||
        this.nodes.input.contains(evt.target) ||
        evt.target.isEqualNode(this.nodes.result) ||
        this.nodes.result.contains(evt.target)
      ) {
        this.resultNodeClicked = true;
      } else {
        this.close();
      }
    }
  }

  handleInputFocus(evt) {
    if (evt.target.value.length > 0) {
      this.search();
    }

    return true;
  }

  handleInputBlur() {
    // This has to be done async, to wait for the focus to be on the next
    // element and avoid closing the results.
    // Example: Going from the input to the reset button.
    // eslint-disable-next-line func-names, consistent-return
    setTimeout(() => {
      if (document.activeElement.isEqualNode(this.nodes.reset)) {
        return false;
      }

      if (this.resultNodeClicked) {
        this.resultNodeClicked = false;
        return false;
      }

      this.close();
    });

    return true;
  }

  addAccessibilityAnnouncer() {
    this.accessibilityAnnouncerDiv = window.document.createElement('div');

    this.accessibilityAnnouncerDiv.setAttribute(
      'style',
      'position: absolute !important; overflow: hidden; clip: rect(0 0 0 0); height: 1px; width: 1px; margin: -1px; padding: 0; border: 0;',
    );

    this.accessibilityAnnouncerDiv.setAttribute('data-search-announcer', '');
    this.accessibilityAnnouncerDiv.setAttribute('aria-live', 'polite');
    this.accessibilityAnnouncerDiv.setAttribute('aria-atomic', 'true');

    this.nodes.result.parentElement.appendChild(this.accessibilityAnnouncerDiv);
  }

  removeAccessibilityAnnouncer() {
    this.nodes.result.parentElement.removeChild(this.accessibilityAnnouncerDiv);
  }

  updateAccessibilityAttributesAfterSelectingElement(
    previousSelectedElement,
    currentSelectedElement,
  ) {
    // Update the active descendant on the search input
    this.nodes.input.setAttribute('aria-activedescendant', currentSelectedElement.id);

    // Unmark the previousSelected elemented as selected
    if (previousSelectedElement) {
      previousSelectedElement.removeAttribute('aria-selected');
    }

    // Mark the element as selected
    currentSelectedElement.setAttribute('aria-selected', true);
  }

  clearAriaActiveDescendant() {
    this.nodes.input.setAttribute('aria-activedescendant', '');
  }

  announceNumberOfResultsFound(results) {
    const currentAnnouncedMessage = this.accessibilityAnnouncerDiv.innerHTML;
    let newMessage = this.numberOfResultsTemplateFct(results);

    // If the messages are the same, they won't get announced
    // add white space so it gets announced
    if (currentAnnouncedMessage === newMessage) {
      newMessage += '&nbsp;';
    }

    this.accessibilityAnnouncerDiv.innerHTML = newMessage;
  }

  announceLoadingState() {
    this.accessibilityAnnouncerDiv.innerHTML = this.loadingResultsMessageTemplateFct();
  }

  handleInputKeyup(evt) {
    const UP_ARROW_KEY_CODE = 38;
    const DOWN_ARROW_KEY_CODE = 40;
    const RETURN_KEY_CODE = 13;
    const ESCAPE_KEY_CODE = 27;

    this.toggleClearButtonVisibility();

    if (this.isResultVisible && this.nodes !== null) {
      if (evt.keyCode === UP_ARROW_KEY_CODE) {
        this.navigateOption(evt, 'UP');
        return true;
      }

      if (evt.keyCode === DOWN_ARROW_KEY_CODE) {
        this.navigateOption(evt, 'DOWN');
        return true;
      }

      if (evt.keyCode === RETURN_KEY_CODE) {
        this.selectOption();
        return true;
      }

      if (evt.keyCode === ESCAPE_KEY_CODE) {
        this.close();
      }
    }

    if (evt.target.value.length <= 0) {
      this.close();
      this.setKeyword('');
    } else if (evt.target.value.length > 0) {
      this.search();
    }

    return true;
  }

  handleInputKeydown(evt) {
    const RETURN_KEY_CODE = 13;
    const UP_ARROW_KEY_CODE = 38;
    const DOWN_ARROW_KEY_CODE = 40;

    // Prevent the form default submission if there is a selected option
    if (evt.keyCode === RETURN_KEY_CODE && this.getSelectedOption() !== null) {
      evt.preventDefault();
    }

    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (evt.keyCode === UP_ARROW_KEY_CODE || evt.keyCode === DOWN_ARROW_KEY_CODE) {
      evt.preventDefault();
    }
  }

  handleInputReset(evt) {
    evt.preventDefault();

    this.nodes.input.value = '';
    this.nodes.input.focus();
    this.toggleClearButtonVisibility();
    this.close();

    return true;
  }

  navigateOption(evt, direction) {
    const currentOption = this.getSelectedOption();

    if (!currentOption) {
      const firstOption = this.nodes.result.querySelector(this.selectors.searchResult);
      firstOption.classList.add(this.classes.itemSelected);
      this.updateAccessibilityAttributesAfterSelectingElement(null, firstOption);
    } else if (direction === 'DOWN') {
      const nextOption = currentOption.nextElementSibling;
      if (nextOption) {
        currentOption.classList.remove(this.classes.itemSelected);
        nextOption.classList.add(this.classes.itemSelected);
        this.updateAccessibilityAttributesAfterSelectingElement(currentOption, nextOption);
      }
    } else {
      const previousOption = currentOption.previousElementSibling;
      if (previousOption) {
        currentOption.classList.remove(this.classes.itemSelected);
        previousOption.classList.add(this.classes.itemSelected);
        this.updateAccessibilityAttributesAfterSelectingElement(currentOption, previousOption);
      }
    }
  }

  getSelectedOption() {
    return this.nodes.result.querySelector(`.${this.classes.itemSelected}`);
  }

  selectOption() {
    const selectedOption = this.getSelectedOption();

    if (selectedOption) {
      selectedOption.querySelector('a, button').click();
    }
  }

  search() {
    const newSearchKeyword = this.nodes.input.value;

    if (this.searchKeyword === newSearchKeyword) {
      return;
    }

    clearTimeout(this.latencyTimer);
    // eslint-disable-next-line func-names
    this.latencyTimer = setTimeout(function() {
      this.results.isLoading = true;

      // Annonouce that we're loading the results
      this.announceLoadingState();

      this.nodes.result.classList.add(this.classes.visibleVariant);
      // NOTE: We could benifit in using DOMPurify.
      // https://github.com/cure53/DOMPurify
      this.nodes.result.innerHTML = searchResultsTemplate(this.results);
    }, 500);

    this.setKeyword(newSearchKeyword);

    fetch(
      `${theme.routes.search}/suggest.json?q=${encodeURIComponent(newSearchKeyword)}&${
        this.configParams
      }`,
    )
      .then(res => res.json())
      .then(res => {
        this.handlePredictiveSearchSuccess(res);
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.log(err);
        this.handlePredictiveSearchError();
      });
  }

  handlePredictiveSearchSuccess(json) {
    clearTimeout(this.latencyTimer);
    this.results = json.resources.results;
    this.totalResults = 0;
    this.results.isLoading = false;
    let resultsLengthCheck = false; // eslint-disable-line no-unused-vars
    this.resultPromises = [];
    this.translatedResults = {
      articles: [],
      products: [],
      pages: [],
      collections: [],
    };

    if (this.results.articles) {
      this.totalResults += this.results.articles.length;
      if (this.results.articles.length > 0) {
        resultsLengthCheck = true;
      }
    }

    if (this.results.collections) {
      this.totalResults += this.results.collections.length;
      if (this.results.collections.length > 0) {
        resultsLengthCheck = true;
      }
    }

    if (this.results.products) {
      if (this.results.products.length > 0) {
        resultsLengthCheck = true;
      }
      this.totalResults += this.results.products.length;
    }

    if (this.results.pages) {
      this.totalResults += this.results.pages.length;
      if (this.results.pages.length > 0) {
        resultsLengthCheck = true;
      }
    }

    if (this.results.products) {
      this.results.products = this.results.products.slice(0, this.numberOfResults);
      this.results.products = this.results.products.filter(
        product => product.tags.indexOf('hidden') === -1,
      );
    } else {
      this.results.products = [];
    }

    this.results.canLoadMore = this.numberOfResults <= this.results.products.length;
    this.results.searchQuery = this.nodes.input.value;

    if (this.results.products.length > 0 || this.results.searchQuery) {
      this.nodes.result.innerHTML = searchResultsTemplate(this.results, this.translatedResults);
      this.announceNumberOfResultsFound(this.results);
      this.open();
    } else {
      this.nodes.result.innerHTML = '';

      this.closeOnNoResults();
    }
  }

  handlePredictiveSearchError() {
    clearTimeout(this.latencyTimer);
    this.nodes.result.innerHTML = '';

    this.closeOnNoResults();
  }

  closeOnNoResults() {
    if (this.nodes) {
      this.nodes.result.classList.remove(this.classes.visibleVariant);
    }

    this.isResultVisible = false;
  }

  setKeyword(keyword) {
    this.searchKeyword = keyword;
  }

  toggleClearButtonVisibility() {
    if (!this.nodes.reset) {
      return;
    }

    if (this.nodes.input.value.length > 0) {
      this.nodes.reset.classList.add(this.classes.clearButtonVisible);
    } else {
      this.nodes.reset.classList.remove(this.classes.clearButtonVisible);
    }
  }

  open() {
    if (this.isResultVisible) {
      return;
    }

    this.nodes.result.classList.add(this.classes.visibleVariant);
    this.nodes.input.setAttribute('aria-expanded', true);
    this.isResultVisible = true;

    // eslint-disable-next-line consistent-return
    return true;
  }

  close() {
    if (!this.isResultVisible) {
      return true;
    }

    if (this.nodes) {
      this.nodes.result.classList.remove(this.classes.visibleVariant);
    }

    this.nodes.input.setAttribute('aria-expanded', false);
    this.clearAriaActiveDescendant();
    this.setKeyword('');

    this.isResultVisible = false;
    this.results = {};

    return true;
  }

  destroy() {
    this.close();

    this.nodes.result.classList.remove(this.classes.visibleVariant);
    this.removeInputAttributes(this.nodes.input);
    this.removeInputEventListeners();
    this.removeBodyEventListener();
    this.removeAccessibilityAnnouncer();
    this.removeClearButtonEventListener();

    return true;
  }

  clearAndClose() {
    this.nodes.input.value = '';
    this.close();
  }
}
